/**
 * RegressionSuite Component
 * Create test run from folder
 * Display test run progress
 * List all test cases in run
 * Support bulk status updates
 */

import React, { useState, useEffect } from 'react';
import {
  apiClient,
  Folder,
  TestRunCreate,
  TestRunWithProgress,
  TestExecution,
  TestExecutionUpdate,
  TestCase,
} from '../services/api';
import './RegressionSuite.css';

interface RegressionSuiteProps {
  folder: Folder;
  onTestCaseSelect?: (testCase: TestCase, execution: TestExecution) => void;
}

export const RegressionSuite: React.FC<RegressionSuiteProps> = ({
  folder,
  onTestCaseSelect,
}) => {
  const [testRun, setTestRun] = useState<TestRunWithProgress | null>(null);
  const [executions, setExecutions] = useState<TestExecution[]>([]);
  const [testCases, setTestCases] = useState<Map<string, TestCase>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [runName, setRunName] = useState('');
  const [buildVersion, setBuildVersion] = useState('');
  const [environment, setEnvironment] = useState('');
  const [selectedExecutions, setSelectedExecutions] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<string>('Pass');

  /**
   * Load test run and executions
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (testRun) {
      loadExecutions();
    }
  }, [testRun]);

  /**
   * Load test executions for the current test run
   */
  const loadExecutions = async () => {
    if (!testRun) return;

    setIsLoading(true);
    setError(null);
    try {
      const executionData = await apiClient.getTestRunExecutions(testRun.id);
      setExecutions(executionData);

      // Load test case details
      await loadTestCases(executionData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load executions');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load test case details for executions
   */
  const loadTestCases = async (executions: TestExecution[]) => {
    const caseMap = new Map<string, TestCase>();
    const uniqueCaseIds = [...new Set(executions.map((e) => e.test_case_id))];

    await Promise.all(
      uniqueCaseIds.map(async (caseId) => {
        try {
          const testCase = await apiClient.getTestCase(caseId);
          caseMap.set(caseId, testCase);
        } catch (err) {
          console.error(`Failed to load test case ${caseId}:`, err);
        }
      })
    );

    setTestCases(caseMap);
  };

  /**
   * Create a new test run
   */
  const handleCreateTestRun = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!runName.trim()) {
      setError('Run name is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const createData: TestRunCreate = {
        name: runName,
        folder_id: folder.id,
        build_version: buildVersion || undefined,
        environment: environment || undefined,
      };

      const newTestRun = await apiClient.createTestRun(createData);
      setTestRun(newTestRun);
      setShowCreateForm(false);
      setRunName('');
      setBuildVersion('');
      setEnvironment('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create test run');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refresh test run progress
   */
  const handleRefreshProgress = async () => {
    if (!testRun) return;

    setIsLoading(true);
    setError(null);
    try {
      const updatedRun = await apiClient.getTestRun(testRun.id);
      setTestRun(updatedRun);
      await loadExecutions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh progress');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggle execution selection
   */
  const toggleExecutionSelection = (executionId: string) => {
    const newSelection = new Set(selectedExecutions);
    if (newSelection.has(executionId)) {
      newSelection.delete(executionId);
    } else {
      newSelection.add(executionId);
    }
    setSelectedExecutions(newSelection);
  };

  /**
   * Select all executions
   */
  const selectAllExecutions = () => {
    setSelectedExecutions(new Set(executions.map((e) => e.id)));
  };

  /**
   * Deselect all executions
   */
  const deselectAllExecutions = () => {
    setSelectedExecutions(new Set());
  };

  /**
   * Apply bulk status update
   */
  const handleBulkStatusUpdate = async () => {
    if (selectedExecutions.size === 0) {
      setError('No executions selected');
      return;
    }

    if (!testRun) return;

    setIsLoading(true);
    setError(null);
    try {
      const updateData: TestExecutionUpdate = {
        execution_status: bulkStatus as 'PASS' | 'FAIL' | 'BLOCKED',
      };

      // Update each selected execution
      await Promise.all(
        Array.from(selectedExecutions).map(async (executionId) => {
          const execution = executions.find((e) => e.id === executionId);
          if (execution) {
            await apiClient.updateExecution(
              execution.id,
              updateData
            );
          }
        })
      );

      // Refresh executions
      await loadExecutions();
      await handleRefreshProgress();
      setSelectedExecutions(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update executions');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get status badge class
   */
  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'Pass':
        return 'status-pass';
      case 'Fail':
        return 'status-fail';
      case 'Blocked':
        return 'status-blocked';
      case 'Not Run':
      default:
        return 'status-not-run';
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="regression-suite">
      <div className="regression-suite-header">
        <h2>Regression Suite: {folder.name}</h2>
        {!testRun && (
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateForm(true)}
            disabled={isLoading}
          >
            + Create Test Run
          </button>
        )}
      </div>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      {/* Create Test Run Form */}
      {showCreateForm && !testRun && (
        <div className="create-test-run-form">
          <h3>Create New Test Run</h3>
          <form onSubmit={handleCreateTestRun}>
            <div className="form-field">
              <label htmlFor="run-name">Run Name *</label>
              <input
                id="run-name"
                type="text"
                value={runName}
                onChange={(e) => setRunName(e.target.value)}
                placeholder="e.g., Sprint 23 Regression"
                required
                disabled={isLoading}
              />
            </div>

            <div className="form-field">
              <label htmlFor="build-version">Build Version</label>
              <input
                id="build-version"
                type="text"
                value={buildVersion}
                onChange={(e) => setBuildVersion(e.target.value)}
                placeholder="e.g., 1.2.3"
                disabled={isLoading}
              />
            </div>

            <div className="form-field">
              <label htmlFor="environment">Environment</label>
              <input
                id="environment"
                type="text"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
                placeholder="e.g., Staging, Production"
                disabled={isLoading}
              />
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? 'Creating...' : 'Create Test Run'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowCreateForm(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Test Run Progress */}
      {testRun && (
        <>
          <div className="test-run-info">
            <div className="run-details">
              <h3>{testRun.name}</h3>
              <div className="run-meta">
                <span>
                  <strong>Status:</strong> {testRun.status}
                </span>
                {testRun.build_version && (
                  <span>
                    <strong>Build:</strong> {testRun.build_version}
                  </span>
                )}
                {testRun.environment && (
                  <span>
                    <strong>Environment:</strong> {testRun.environment}
                  </span>
                )}
                <span>
                  <strong>Started:</strong> {testRun.started_at ? formatDate(testRun.started_at) : 'Not started'}
                </span>
                <span>
                  <strong>By:</strong> {testRun.started_by}
                </span>
              </div>
            </div>

            <button
              className="btn btn-secondary"
              onClick={handleRefreshProgress}
              disabled={isLoading}
              title="Refresh progress"
            >
              🔄 Refresh
            </button>
          </div>

          {/* Progress Bar */}
          <div className="progress-section">
            <div className="progress-header">
              <h4>Progress</h4>
              <span className="progress-text">
                {testRun.executed_cases} / {testRun.total_cases} executed (
                {testRun.progress_percentage.toFixed(1)}%)
              </span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${testRun.progress_percentage}%` }}
              />
            </div>
          </div>

          {/* Bulk Actions */}
          {executions.length > 0 && (
            <div className="bulk-actions">
              <div className="selection-controls">
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={selectAllExecutions}
                  disabled={isLoading}
                >
                  Select All
                </button>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={deselectAllExecutions}
                  disabled={isLoading}
                >
                  Deselect All
                </button>
                <span className="selection-count">
                  {selectedExecutions.size} selected
                </span>
              </div>

              <div className="bulk-update-controls">
                <label htmlFor="bulk-status">Bulk Update Status:</label>
                <select
                  id="bulk-status"
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="Pass">Pass</option>
                  <option value="Fail">Fail</option>
                  <option value="Blocked">Blocked</option>
                  <option value="Not Run">Not Run</option>
                </select>
                <button
                  className="btn btn-primary"
                  onClick={handleBulkStatusUpdate}
                  disabled={isLoading || selectedExecutions.size === 0}
                >
                  Apply to Selected
                </button>
              </div>
            </div>
          )}

          {/* Test Executions List */}
          <div className="executions-list">
            <h4>Test Cases ({executions.length})</h4>
            {isLoading && executions.length === 0 ? (
              <div className="loading">Loading test cases...</div>
            ) : executions.length === 0 ? (
              <div className="empty-state">
                <p>No test cases in this run</p>
              </div>
            ) : (
              <div className="execution-items">
                {executions.map((execution) => {
                  const testCase = testCases.get(execution.test_case_id);
                  const isSelected = selectedExecutions.has(execution.id);

                  return (
                    <div
                      key={execution.id}
                      className={`execution-item ${isSelected ? 'selected' : ''}`}
                    >
                      <div className="execution-checkbox">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleExecutionSelection(execution.id)}
                          disabled={isLoading}
                          aria-label={`Select test case ${testCase?.test_case_description || execution.test_case_id}`}
                        />
                      </div>

                      <div className="execution-content">
                        <div className="execution-header">
                          <h5>
                            {testCase?.test_case_description.substring(0, 100) ||
                              `Test Case ${execution.test_case_id.substring(0, 8)}...`}
                            {testCase && testCase.test_case_description.length > 100 && '...'}
                          </h5>
                          <span className={`status-badge ${getStatusClass(execution.execution_status)}`}>
                            {execution.execution_status}
                          </span>
                        </div>

                        {execution.comment && (
                          <div className="execution-notes">
                            <strong>Notes:</strong> {execution.comment}
                          </div>
                        )}

                        {execution.executed_at && (
                          <div className="execution-meta">
                            <span>Executed: {formatDate(execution.executed_at)}</span>
                            {execution.executed_by && <span>By: {execution.executed_by}</span>}
                          </div>
                        )}
                      </div>

                      <div className="execution-actions">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => testCase && onTestCaseSelect?.(testCase, execution)}
                          disabled={isLoading || !testCase}
                        >
                          Execute
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
